/**
 * Parses a composite dealer ID string (e.g. "DEUA23680") into a structured object.
 * Format: market (3 uppercase letters) + brand (1 uppercase letter) + id (numeric).
 *
 * Returns undefined if the composite ID is invalid or too short.
 */
export function parseCompositeDealerId(
  compositeId: string | undefined | null,
): { market: string; brand: string; id: string } | undefined {
  if (!compositeId) return undefined;
  if (compositeId.length <= 4) return undefined;

  const market = compositeId.substring(0, 3);
  const brand = compositeId.substring(3, 4);
  const id = compositeId.substring(4);

  if (!/^[A-Z]{3}$/.test(market) || !/^[A-Z]$/.test(brand) || !/^\d+$/.test(id)) {
    return undefined;
  }

  return { market, brand, id };
}

/**
 * Validates a structured dealer ID object.
 * Returns true if market, brand, and id match the expected format.
 */
export function isValidDealerId(dealerContext: {
  market?: string;
  brand?: string;
  id?: string;
}): boolean {
  const { market, brand, id } = dealerContext;
  return (
    !!market &&
    !!brand &&
    !!id &&
    /^[A-Z]{3}$/.test(market) &&
    /^[A-Z]$/.test(brand) &&
    (market === 'USA' ? /^\d{2}[A-Z]\d{2}$/.test(id) : /^\d+$/.test(id))
  );
}
